import React, { createContext, useContext, useMemo } from 'react';
import { useTwilioDevice, type UseTwilioDeviceReturn, type TwilioCallInfo } from '@/hooks/use-twilio-device';
import { useSignalWireDevice } from '@/hooks/use-signalwire-device';
import { useQuery } from '@tanstack/react-query';

interface TwilioDeviceContextValue extends UseTwilioDeviceReturn {}
const TwilioDeviceContext = createContext<TwilioDeviceContextValue | null>(null);

export function TwilioDeviceProvider({ children }: { children: React.ReactNode }) {
  const { data: user } = useQuery<{ id: string; email: string }>({
    queryKey: ['/api/auth/user'], staleTime: Infinity,
  });
  const { data: commStatus } = useQuery<{ configured: boolean; adapter: string }>({
    queryKey: ['/api/calls/config/status'], staleTime: 60000, enabled: !!user,
  });

  const isConfigured = commStatus?.configured && commStatus?.adapter !== 'mock';
  const isSignalWire = commStatus?.adapter === 'signalwire';

  const twilio = useTwilioDevice(isConfigured && !isSignalWire ? user?.id : undefined);
  const signalwire = useSignalWireDevice(isConfigured && isSignalWire ? user?.id : undefined);

  const device = isSignalWire ? signalwire : twilio;

  const value = useMemo(() => device, [
    device.isReady, device.callState, device.activeCallInfo, device.callDuration, device.isMuted, device.error,
  ]);

  return <TwilioDeviceContext.Provider value={value}>{children}</TwilioDeviceContext.Provider>;
}

export function useTwilioCall(): TwilioDeviceContextValue {
  const ctx = useContext(TwilioDeviceContext);
  if (!ctx) throw new Error('useTwilioCall must be used within TwilioDeviceProvider');
  return ctx;
}

export type { TwilioCallInfo };
